/**
 * plaidConnectionMonitorService.ts
 * 
 * VS-PLAID-CONNECTION-MONITORING: Admin service for connection failure monitoring
 * 
 * Calls vs-integrations API endpoints:
 * - GET /plaid/connection-cases (list all cases)
 * - GET /plaid/connection-case?case_id=<id> (get case details with events)
 * - PATCH /plaid/connection-case?case_id=<id> (update case)
 */

const INTEGRATIONS_API =
  import.meta.env.VITE_INTEGRATIONS_API_URL ||
  'https://vs-integrations-development.netlify.app';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;
const API_BASE = import.meta.env.VITE_API_URL || '/api';

// ─── Types (mirror server DTOs) ────────────────────────────────────────────

export interface PlaidLinkSession {
  id: number;
  correlation_id: string;
  link_session_id?: string; // Plaid-assigned session ID
  member_id: string | null;
  context: string;
  flow_type: string;
  terminal_outcome: string | null;
  started_at: string;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlaidLinkEvent {
  id: number;
  session_id: number;
  event_name: string;
  event_timestamp: string;
  received_at: string;
  source: string;
  request_id?: string; // Plaid request ID for support
  institution_id: string | null;
  institution_name: string | null;
  error_code: string | null;
  error_type: string | null;
  error_message_sanitized: string | null;
  created_at: string;
}

export interface ConnectionCase {
  id: number;
  session_id: number;
  classification: string | null;
  status: string;
  member_impact: string | null;
  priority: string;
  retry_attempt_number: number;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
  resolved_at: string | null;
  plaid_link_sessions?: PlaidLinkSession;

  // Member display fields (from migration 20260729_PLAID-CONN-004)
  member_name?: string;
  member_email?: string;
  member_auth0_id?: string;
  member_id?: string;
  institution_name?: string;
}

export interface CaseListResponse {
  status: string;
  data: {
    cases: ConnectionCase[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    };
  };
}

export interface CaseDetailResponse {
  status: string;
  data: {
    case: ConnectionCase;
    events: PlaidLinkEvent[];
  };
}

export interface UpdateCaseRequest {
  classification?: string;
  status?: string;
  diagnosis?: string;
  recommended_action?: string;
}

// ─── FI Health Check Types (from FI Confirmation tool) ─────────────────────────────────────────

export interface FiCheckAuthMetadata {
  supported_methods?: Record<string, boolean> | null;
}

export interface FiCheckMatch {
  institution_id: string;
  name: string;
  oauth: boolean;
  url: string | null;
  logo: string | null;
  primary_color: string | null;
  products: string[];
  routing_numbers: string[];
  auth_metadata: FiCheckAuthMetadata | null;
  health_status: string;
  status_detail: Record<string, unknown> | null;
}

export interface FiCheckResponse {
  query: string | null;
  routing_numbers: string[] | null;
  lookup_method: string;
  filters: {
    oauth: boolean | null;
    products: string[] | null;
    include_auth_metadata: boolean;
  };
  supported: boolean;
  match_count: number;
  env: string;
  matches: FiCheckMatch[];
}

// ─── API Functions ──────────────────────────────────────────────────────────

/**
 * Get user's access token from session
 * Uses the same pattern as DataHealthDashboard - fetch from /verascore/session-token
 * The session cookie is sent automatically via credentials: 'include'
 */
async function getAccessToken(): Promise<string> {
  // Try session storage cache first
  const cached = sessionStorage.getItem('vs_admin_token');
  if (cached) {
    if (import.meta.env.DEV) console.log('[FI Investigation] Using cached token');
    return cached;
  }

  if (import.meta.env.DEV) console.log('[FI Investigation] Fetching session token from', `${API_BASE}/verascore/session-token`);

  // Fetch from backend session
  const res = await fetch(`${API_BASE}/verascore/session-token`, {
    method: 'GET',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (!res.ok) {
    const errorText = await res.text();
    if (import.meta.env.DEV) console.error('[FI Investigation] Session token request failed:', res.status, errorText);
    throw new Error(`Session expired (${res.status}). Please refresh the page.`);
  }

  const data = await res.json();
  if (import.meta.env.DEV) console.log('[FI Investigation] Session token response:', data);

  const token = data?.data?.access_token;
  if (!token) {
    if (import.meta.env.DEV) console.error('[FI Investigation] No access_token in response:', data);
    throw new Error('No access token in session response.');
  }

  // Cache for future requests
  sessionStorage.setItem('vs_admin_token', token);
  if (import.meta.env.DEV) console.log('[FI Investigation] Token cached successfully');
  return token;
}

/**
 * List all connection failure cases with search/filter support
 */
export async function listConnectionCases(
  limit = 50,
  offset = 0,
  filters?: {
    status?: string;
    classification?: string;
    searchQuery?: string;
    memberEmail?: string;
    institutionName?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: string;
  }
): Promise<CaseListResponse> {
  const token = await getAccessToken();

  const url = new URL(`${INTEGRATIONS_API}/plaid/connection-cases`);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('offset', String(offset));

  // Add filter params
  if (filters?.status) url.searchParams.set('status', filters.status);
  if (filters?.classification) url.searchParams.set('classification', filters.classification);
  if (filters?.searchQuery) url.searchParams.set('search', filters.searchQuery);
  if (filters?.memberEmail) url.searchParams.set('member_email', filters.memberEmail);
  if (filters?.institutionName) url.searchParams.set('institution_name', filters.institutionName);
  if (filters?.dateFrom) url.searchParams.set('date_from', filters.dateFrom);
  if (filters?.dateTo) url.searchParams.set('date_to', filters.dateTo);
  if (filters?.sortBy) url.searchParams.set('sort_by', filters.sortBy);
  if (filters?.sortOrder) url.searchParams.set('sort_order', filters.sortOrder);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'VS-Client-Id': VS_CLIENT_ID,
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    // If 401, the token might be expired - clear cache and retry once
    if (response.status === 401) {
      if (import.meta.env.DEV) console.warn('[FI Investigation] 401 Unauthorized - token may be expired, clearing cache and retrying');
      sessionStorage.removeItem('vs_admin_token');

      // Retry with fresh token
      const newToken = await getAccessToken();
      const retryResponse = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'VS-Client-Id': VS_CLIENT_ID,
          Authorization: `Bearer ${newToken}`,
        },
      });

      if (!retryResponse.ok) {
        const errorData = await retryResponse.json().catch(() => ({}));
        throw new Error(errorData.data?.reason || `HTTP ${retryResponse.status}`);
      }

      return retryResponse.json();
    }

    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.data?.reason || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Get case details with full event timeline
 */
export async function getCaseDetails(caseId: number): Promise<CaseDetailResponse> {
  const token = await getAccessToken();
  
  const url = new URL(`${INTEGRATIONS_API}/plaid/connection-case`);
  url.searchParams.set('case_id', String(caseId));

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'VS-Client-Id': VS_CLIENT_ID,
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.data?.reason || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Update case classification/status
 */
export async function updateCase(
  caseId: number,
  updates: UpdateCaseRequest
): Promise<CaseDetailResponse> {
  const token = await getAccessToken();
  
  const url = new URL(`${INTEGRATIONS_API}/plaid/connection-case`);
  url.searchParams.set('case_id', String(caseId));

  const response = await fetch(url.toString(), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'VS-Client-Id': VS_CLIENT_ID,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.data?.reason || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Check institution health status
 * Used to auto-fetch FI health when viewing a case
 */
export async function checkInstitutionHealth(
  institutionIdOrName: string
): Promise<FiCheckResponse> {
  const token = await getAccessToken();

  const url = new URL(`${INTEGRATIONS_API}/plaid/institution-check`);
  url.searchParams.set('query', institutionIdOrName);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'VS-Client-Id': VS_CLIENT_ID,
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.data?.reason || `HTTP ${response.status}`);
  }

  return response.json();
}
